import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function ChangePassword() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const mustChange = !!user?.must_change_password;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      await refresh?.();
      toast.success("Password updated");
      navigate(user?.role === "owner" ? "/dashboard" : "/map");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-10" data-testid="change-password-page">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-5"
      >
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Change password</h1>
          {mustChange ? (
            <p
              className="mt-2 text-sm rounded-lg border border-primary/30 bg-primary/10 text-foreground px-3 py-2"
              data-testid="must-change-banner"
            >
              Your password was reset by an admin. Please set a new password to continue.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground mt-1">
              Enter your current password and pick a new one.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="current">Current password</Label>
          <Input
            id="current"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
            data-testid="change-current-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="next">New password</Label>
          <Input
            id="next"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="change-new-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm">Confirm new password</Label>
          <Input
            id="confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="change-confirm-input"
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-full"
          data-testid="change-submit-btn"
        >
          {loading ? "Saving…" : "Save new password"}
        </Button>
      </form>
    </div>
  );
}
